import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import { toast } from "sonner";
import { useAuth } from "@/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Lock, CheckCircle2, Sparkles, FileText, Download, ArrowLeft, Loader2, AlertTriangle,
  ShieldCheck, Route, XCircle, TrendingUp, ChevronRight, Globe, Map as MapIcon, Layers, Activity, Calculator,
} from "lucide-react";
import { NoctuaContext } from "@/pages/noctua/NoctuaContext";
import MapaPage from "@/pages/noctua/MapaPage";
import LecturaPage from "@/pages/noctua/LecturaPage";
import InteligenciaPage from "@/pages/noctua/InteligenciaPage";
import EscenariosPage from "@/pages/noctua/EscenariosPage";
import "@/pages/noctua/noctua.css";

const PHASES = [
  { key: "fase00", num: "00", title: "Inteligencia Electoral", desc: "Mapa · Lectura · Inteligencia · Escenarios (adscrito a tu subcuenta)" },
  { key: "fase01", num: "01", title: "Diagnóstico Inicial (DVP)", desc: "Análisis de viabilidad política con IA · Claude Sonnet 4.5" },
  { key: "fase02", num: "02", title: "Arqueología del Territorio", desc: "Validación de coherencia · 4 checks automáticos" },
  { key: "fase03", num: "03", title: "Levantamiento Estratégico", desc: "Árbol territorial por tipo de cargo · escenarios y hoja de ruta" },
];

export default function ModuloViabilidad() {
  const [activePhase, setActivePhase] = useState("fase00");
  const [reports, setReports] = useState([]);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    api.get("/dvp/reports").then((r) => {
      setReports(r.data);
      if (r.data.length > 0) setCurrent(r.data[0]);
    }).catch(() => {});
  }, []);

  const fase01Complete = !!current && !current.report?.error;
  const fase02Complete = !!current?.fase02 && current.fase02.validacion_coherencia === "APROBADO";
  const fase03Complete = !!current?.fase03 && !current.fase03.error;

  const canOpen = (key) => {
    if (key === "fase00") return true;
    if (key === "fase01") return true;
    if (key === "fase02") return fase01Complete;
    if (key === "fase03") return fase02Complete;
    return false;
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <div className="flex items-center gap-3 text-sm text-slate-500 mb-2">
        <Link to="/dashboard" className="hover:text-slate-800 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </Link>
        <span>·</span>
        <span className="text-slate-700 font-medium">Módulo 1 · Viabilidad</span>
      </div>

      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Módulo 1 · Viabilidad</h1>
        <p className="text-slate-600 text-sm mt-1">
          Diagnóstico secuencial DVP v2.1. Cada fase se desbloquea al aprobar la anterior con el reporte activo.
        </p>
      </div>

      {/* Report selector (hidden on Fase 00 which does not use reports) */}
      {activePhase !== "fase00" && reports.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-widest text-slate-500">Aspirante activo:</span>
          {reports.map((r) => (
            <button
              key={r.id}
              onClick={() => { setCurrent(r); setActivePhase("fase01"); }}
              className={`text-xs px-3 py-1.5 rounded-full border ${
                current?.id === r.id
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
              }`}
              data-testid={`aspirante-pill-${r.id}`}
            >
              {r.report?.meta?.aspirante || r.inputs?.aspirante || "(sin nombre)"}
            </button>
          ))}
          <button
            onClick={() => { setCurrent(null); setActivePhase("fase01"); }}
            className="text-xs px-3 py-1.5 rounded-full border border-dashed border-slate-300 text-slate-600 hover:border-slate-500"
            data-testid="new-aspirante"
          >
            + Nuevo aspirante
          </button>
        </div>
      )}

      {/* Phase stepper */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {PHASES.map((p) => {
          const isActive = activePhase === p.key;
          const unlocked = canOpen(p.key);
          const done = (p.key === "fase01" && fase01Complete) || (p.key === "fase02" && fase02Complete) || (p.key === "fase03" && fase03Complete);
          return (
            <button
              key={p.key}
              disabled={!unlocked}
              onClick={() => unlocked && setActivePhase(p.key)}
              title={!unlocked ? "Completa la fase anterior" : ""}
              className={`text-left border rounded-lg p-4 transition-all ${
                isActive ? "border-blue-500 bg-blue-50/50 shadow-sm"
                : !unlocked ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
                : "border-slate-200 hover:border-slate-400 bg-white"
              }`}
              data-testid={`phase-tab-${p.key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  done ? "bg-emerald-500 text-white" : isActive ? "bg-blue-500 text-white" : !unlocked ? "bg-slate-200 text-slate-400" : "bg-slate-200 text-slate-600"
                }`}>
                  {done ? <CheckCircle2 className="w-4 h-4" /> : p.num}
                </div>
                {!unlocked && <Lock className="w-4 h-4 text-slate-400" />}
              </div>
              <div className="font-display font-bold text-sm text-slate-900">Fase {p.num}</div>
              <div className="text-slate-700 text-sm">{p.title}</div>
              <div className="text-slate-500 text-xs mt-1">{p.desc}</div>
            </button>
          );
        })}
      </div>

      {activePhase === "fase00" && <Fase00Inteligencia />}
      {activePhase === "fase01" && (
        <Fase01DVP
          reports={reports}
          current={current}
          onNew={(r) => { setReports([r, ...reports]); setCurrent(r); }}
          onSelect={setCurrent}
        />
      )}
      {activePhase === "fase02" && (
        <Fase02Coherencia current={current} onUpdate={(r) => { setCurrent(r); setReports(reports.map(x => x.id === r.id ? r : x)); }} />
      )}
      {activePhase === "fase03" && (
        <Fase03Territorio current={current} onUpdate={(r) => { setCurrent(r); setReports(reports.map(x => x.id === r.id ? r : x)); }} />
      )}
    </div>
  );
}

/* ================================ FASE 01 ================================ */
function Fase01DVP({ reports, current, onNew }) {
  const [form, setForm] = useState({
    aspirante: "", cargo: "", partido: "", distrito_municipio: "",
    semblanza: "", info_adicional: "",
  });
  const [loading, setLoading] = useState(false);

  const runDVP = async () => {
    if (!form.aspirante || !form.cargo || !form.semblanza) {
      toast.error("Aspirante, cargo y semblanza son obligatorios");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/dvp/analyze", form);
      toast.success("Diagnóstico completado");
      onNew(r.data);
      setForm({ aspirante: "", cargo: "", partido: "", distrito_municipio: "", semblanza: "", info_adicional: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en el análisis DVP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-blue-500" />
          <h2 className="font-display font-bold text-lg">Datos del aspirante</h2>
        </div>
        <p className="text-xs text-slate-500 -mt-2">
          El agente DVP analizará el perfil, verificará información pública, corregirá encuadres y emitirá un veredicto estructurado.
        </p>
        <Field label="Nombre completo del aspirante *">
          <Input value={form.aspirante} onChange={(e) => setForm({ ...form, aspirante: e.target.value })} placeholder="Ej. María López García" data-testid="dvp-aspirante" />
        </Field>
        <Field label="Cargo al que aspira *">
          <Input value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} placeholder="Ej. Diputada Local por reelección" data-testid="dvp-cargo" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Partido">
            <Input value={form.partido} onChange={(e) => setForm({ ...form, partido: e.target.value })} placeholder="Ej. MC" data-testid="dvp-partido" />
          </Field>
          <Field label="Distrito o municipio">
            <Input value={form.distrito_municipio} onChange={(e) => setForm({ ...form, distrito_municipio: e.target.value })} placeholder="Ej. Cuernavaca, Morelos" data-testid="dvp-distrito" />
          </Field>
        </div>
        <Field label="Semblanza / trayectoria *">
          <Textarea rows={5} value={form.semblanza} onChange={(e) => setForm({ ...form, semblanza: e.target.value })} placeholder="Formación, cargos previos, activos, filiaciones, iniciativas destacadas..." data-testid="dvp-semblanza" />
        </Field>
        <Field label="Información adicional (opcional)">
          <Textarea rows={2} value={form.info_adicional} onChange={(e) => setForm({ ...form, info_adicional: e.target.value })} data-testid="dvp-info-adicional" />
        </Field>
        <Button onClick={runDVP} disabled={loading} className="w-full btn-primary" data-testid="dvp-run">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizando con Claude...</> : <><Sparkles className="w-4 h-4 mr-2" /> Ejecutar diagnóstico DVP</>}
        </Button>
      </Card>

      <div className="space-y-4">
        {loading && (
          <Card className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-3" />
            <div className="font-display font-medium text-slate-700">El agente DVP está trabajando...</div>
            <div className="text-xs text-slate-500 mt-2">Puede tomar 20-60 segundos.</div>
          </Card>
        )}
        {!loading && !current && (
          <Card className="p-8 text-center border-dashed">
            <FileText className="w-8 h-8 text-slate-400 mx-auto mb-3" />
            <div className="font-display font-medium text-slate-700">Aquí aparecerá el reporte DVP</div>
            <div className="text-xs text-slate-500 mt-1">Completa el formulario y ejecuta el diagnóstico.</div>
          </Card>
        )}
        {!loading && current && <DVPReport report={current.report || current} />}
      </div>
    </div>
  );
}

/* ================================ FASE 02 ================================ */
function Fase02Coherencia({ current, onUpdate }) {
  const [loading, setLoading] = useState(false);

  if (!current) {
    return (
      <Card className="p-6 text-center border-dashed">
        <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
        <div className="text-slate-700">Selecciona un aspirante con reporte de Fase 01 aprobado para continuar.</div>
      </Card>
    );
  }

  const fase02 = current.fase02;
  const processing = current.fase02_status === "processing";

  const runFase02 = async (force = false) => {
    setLoading(true);
    try {
      const r = await api.post(`/dvp/reports/${current.id}/fase02${force ? "?force=true" : ""}`);
      if (r.data.status === "ready") {
        toast.success("Validación de coherencia lista");
        onUpdate({ ...current, fase02: r.data.fase02, fase02_status: "ready" });
        setLoading(false);
        return;
      }
      toast.info("Auditando en segundo plano... (puede tardar hasta 2 min)");
      onUpdate({ ...current, fase02_status: "processing" });
      // Poll
      const interval = setInterval(async () => {
        try {
          const g = await api.get(`/dvp/reports/${current.id}`);
          if (g.data.fase02) {
            clearInterval(interval);
            setLoading(false);
            toast.success("Validación de coherencia completada");
            onUpdate(g.data);
          } else if (g.data.fase02_status === "error") {
            clearInterval(interval);
            setLoading(false);
            toast.error("Error en Fase 02: " + (g.data.fase02_error || "desconocido"));
            onUpdate(g.data);
          }
        } catch (_e) { /* keep polling */ }
      }, 5000);
      setTimeout(() => { clearInterval(interval); setLoading(false); }, 240000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en la validación de coherencia");
      setLoading(false);
    }
  };

  if (!fase02 && !processing && !loading) {
    return (
      <Card className="p-8 text-center">
        <ShieldCheck className="w-10 h-10 text-blue-500 mx-auto mb-3" />
        <h2 className="font-display font-bold text-lg text-slate-800">Arqueología del Territorio</h2>
        <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">
          Ejecutará 4 checks automáticos sobre el reporte de Fase 01 para detectar incoherencias
          antes de avanzar al levantamiento estratégico.
        </p>
        <Button onClick={() => runFase02(false)} disabled={loading} className="mt-6 btn-primary" data-testid="fase02-run">
          <ShieldCheck className="w-4 h-4 mr-2" /> Ejecutar validación de coherencia
        </Button>
      </Card>
    );
  }

  if (loading || processing) {
    return (
      <Card className="p-8 text-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-500 mx-auto mb-3" />
        <div className="font-display font-medium text-slate-700">Auditando coherencia con Claude...</div>
        <div className="text-xs text-slate-500 mt-2">Puede tardar entre 30s y 2min. La UI se actualizará automáticamente.</div>
      </Card>
    );
  }

  if (fase02.error) {
    return <ErrorCard label="Error del modelo" msg={fase02.error} raw={fase02.raw_response} />;
  }

  // Derive status from checks so UI is never inconsistent
  const hasFallas = (fase02.detalle_checks || []).some((c) => c.estatus === "FALLA");
  const derivedStatus = hasFallas ? "ALERTA" : (fase02.validacion_coherencia || "APROBADO");
  const isOk = derivedStatus === "APROBADO";

  const advanceWithOverride = async () => {
    // Try Fase03 with force=true; parent flow will handle polling
    try {
      const r = await api.post(`/dvp/reports/${current.id}/fase03?force=true`);
      toast.success(r.data.status === "ready" ? "Fase 03 lista" : "Fase 03 iniciada");
      onUpdate({ ...current, fase03: r.data.fase03 || null, fase03_status: r.data.status });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo forzar Fase 03");
    }
  };

  return (
    <div className="space-y-5">
      <Card className={`p-6 border ${isOk ? "border-emerald-300 bg-emerald-50/60" : "border-amber-300 bg-amber-50/60"}`}>
        <div className="flex items-center gap-3">
          {isOk ? <CheckCircle2 className="w-8 h-8 text-emerald-600" /> : <AlertTriangle className="w-8 h-8 text-amber-600" />}
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-600">Validación de coherencia</div>
            <div className="font-display font-black text-2xl text-slate-900" data-testid="fase02-status">{derivedStatus}</div>
            <div className="text-xs text-slate-600 mt-1">
              {fase02.checks_ejecutados || (fase02.detalle_checks || []).length} checks ejecutados · {(fase02.alertas || []).length} alertas
            </div>
          </div>
          <div className="ml-auto flex gap-2">
            {!isOk && (
              <Button size="sm" variant="outline" onClick={advanceWithOverride} data-testid="fase02-override">
                Aceptar con alertas y avanzar
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => runFase02(true)} disabled={loading} data-testid="fase02-rerun">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Re-ejecutar"}
            </Button>
          </div>
        </div>
      </Card>

      {/* Checks */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(fase02.detalle_checks || []).map((c) => (
          <div key={c.id} className={`border rounded-lg p-4 ${c.estatus === "OK" ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/50"}`} data-testid={`check-${c.id}`}>
            <div className="flex items-center gap-2 mb-1">
              {c.estatus === "OK" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-amber-600" />}
              <div className="text-xs uppercase tracking-widest text-slate-600">{c.id}</div>
              <Badge variant="outline" className="ml-auto text-[10px]">{c.estatus}</Badge>
            </div>
            <div className="font-display font-bold text-sm text-slate-900">{c.titulo}</div>
            <div className="text-slate-700 text-sm mt-1">{c.hallazgo}</div>
          </div>
        ))}
      </div>

      {fase02.narrativa_arqueologica && (
        <Card className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Narrativa arqueológica</div>
          <p className="text-slate-800 leading-relaxed text-sm whitespace-pre-wrap">{fase02.narrativa_arqueologica}</p>
        </Card>
      )}

      {fase02.correcciones_sugeridas && (
        <Card className="p-5 border-amber-200 bg-amber-50/40">
          <div className="text-xs uppercase tracking-widest text-slate-600 mb-2">Correcciones sugeridas</div>
          <div className="text-sm text-slate-800 space-y-1">
            {fase02.correcciones_sugeridas.tipo_contienda && (
              <div><strong>Tipo de contienda:</strong> {fase02.correcciones_sugeridas.tipo_contienda}</div>
            )}
            {fase02.correcciones_sugeridas.tipo_aspiracion && (
              <div><strong>Tipo de aspiración:</strong> {fase02.correcciones_sugeridas.tipo_aspiracion}</div>
            )}
            {(fase02.correcciones_sugeridas.riesgos_agregar || []).map((r, i) => (
              <div key={i} className="mt-2 pl-3 border-l-2 border-amber-400">
                <div className="text-xs text-slate-500 uppercase tracking-widest">{r.categoria}</div>
                <div>{r.descripcion}</div>
                <div className="text-xs text-slate-600 mt-1"><em>Acción:</em> {r.accion_recomendada}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {fase02.recomendacion_para_fase03 && (
        <Card className="p-5 border-blue-200 bg-blue-50/40">
          <div className="flex items-center gap-2 mb-2">
            <Route className="w-4 h-4 text-blue-600" />
            <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">Recomendación para Fase 03</div>
          </div>
          <p className="text-slate-800 text-sm leading-relaxed">{fase02.recomendacion_para_fase03}</p>
        </Card>
      )}
    </div>
  );
}

/* ================================ FASE 03 ================================ */
function Fase03Territorio({ current, onUpdate }) {
  const [loading, setLoading] = useState(false);

  if (!current || !current.fase02) {
    return (
      <Card className="p-6 text-center border-dashed">
        <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
        <div className="text-slate-700">Debes aprobar la Fase 02 antes de ejecutar el levantamiento estratégico.</div>
      </Card>
    );
  }

  const fase03 = current.fase03;
  const processing = current.fase03_status === "processing";

  const runFase03 = async (force = false) => {
    setLoading(true);
    try {
      const r = await api.post(`/dvp/reports/${current.id}/fase03${force ? "?force=true" : ""}`);
      if (r.data.status === "ready") {
        toast.success("Levantamiento estratégico listo");
        onUpdate({ ...current, fase03: r.data.fase03, fase03_status: "ready" });
        setLoading(false);
        return;
      }
      toast.info("Ejecutando en segundo plano... (puede tardar hasta 2 min)");
      onUpdate({ ...current, fase03_status: "processing" });
      const interval = setInterval(async () => {
        try {
          const g = await api.get(`/dvp/reports/${current.id}`);
          if (g.data.fase03) {
            clearInterval(interval);
            setLoading(false);
            toast.success("Levantamiento estratégico completado");
            onUpdate(g.data);
          } else if (g.data.fase03_status === "error") {
            clearInterval(interval);
            setLoading(false);
            toast.error("Error en Fase 03: " + (g.data.fase03_error || "desconocido"));
            onUpdate(g.data);
          }
        } catch (_e) { /* keep polling */ }
      }, 5000);
      setTimeout(() => { clearInterval(interval); setLoading(false); }, 240000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en el levantamiento estratégico");
      setLoading(false);
    }
  };

  if (!fase03 && !processing && !loading) {
    return (
      <Card className="p-8 text-center">
        <Route className="w-10 h-10 text-blue-500 mx-auto mb-3" />
        <h2 className="font-display font-bold text-lg text-slate-800">Levantamiento Estratégico</h2>
        <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">
          Ejecutará el árbol de preguntas específico al tipo de cargo detectado
          (Diputación Local / Presidencia Municipal / Gubernatura / Diputación Federal)
          y devolverá 3 escenarios + hoja de ruta a 90 días.
        </p>
        <Button onClick={() => runFase03(false)} disabled={loading} className="mt-6 btn-primary" data-testid="fase03-run">
          <Route className="w-4 h-4 mr-2" /> Ejecutar levantamiento estratégico
        </Button>
      </Card>
    );
  }

  if (loading || processing) {
    return (
      <Card className="p-8 text-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-500 mx-auto mb-3" />
        <div className="font-display font-medium text-slate-700">Levantando el territorio con Claude...</div>
        <div className="text-xs text-slate-500 mt-2">Puede tardar entre 60s y 2min. La UI se actualizará automáticamente.</div>
      </Card>
    );
  }

  if (fase03.error) {
    return <ErrorCard label="Error del modelo" msg={fase03.error} raw={fase03.raw_response} />;
  }

  return (
    <div className="space-y-5" data-testid="fase03-report">
      <Card className="p-6 border-blue-200 bg-blue-50/40">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-8 h-8 text-blue-600" />
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-600">Tipo de cargo detectado</div>
            <div className="font-display font-black text-xl text-slate-900 uppercase">
              {(fase03.tipo_cargo_detectado || "—").replace(/_/g, " ")}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => runFase03(true)} disabled={loading} className="ml-auto" data-testid="fase03-rerun">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Re-ejecutar"}
          </Button>
        </div>
      </Card>

      {/* Preguntas estratégicas */}
      {fase03.preguntas_estrategicas?.length > 0 && (
        <Card className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Árbol de preguntas estratégicas</div>
          <div className="space-y-3">
            {fase03.preguntas_estrategicas.map((q) => (
              <div key={q.id} className="border-l-2 border-blue-400 pl-4" data-testid={`pregunta-${q.id}`}>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">{q.id}</Badge>
                  <Badge className={
                    q.impacto_estrategico === "alto" ? "bg-red-100 text-red-800" :
                    q.impacto_estrategico === "medio" ? "bg-amber-100 text-amber-800" :
                    "bg-slate-100 text-slate-700"
                  }>impacto {q.impacto_estrategico}</Badge>
                </div>
                <div className="font-display font-bold text-sm text-slate-900 mt-1">{q.pregunta}</div>
                <div className="text-sm text-slate-700 mt-1">{q.respuesta}</div>
                {q.fuente && <div className="text-xs text-slate-500 mt-1 italic">Fuente: {q.fuente}</div>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Campo electoral enriquecido */}
      {fase03.campo_electoral_enriquecido && (
        <Card className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Campo electoral enriquecido</div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <MetaBox label="Incumbente" value={fase03.campo_electoral_enriquecido.incumbente} />
            <MetaBox label="Partido incumbente" value={fase03.campo_electoral_enriquecido.partido_incumbente} />
            <MetaBox label="Correlación de fuerzas" value={fase03.campo_electoral_enriquecido.correlacion_fuerzas_pct} />
            <MetaBox label="Restricción de paridad" value={fase03.campo_electoral_enriquecido.restriccion_paridad ? "Sí" : "No"} />
            <MetaBox label="Proceso de selección" value={fase03.campo_electoral_enriquecido.proceso_seleccion_interna} />
            <MetaBox label="Detalle paridad" value={fase03.campo_electoral_enriquecido.detalle_paridad} />
          </div>
          {fase03.campo_electoral_enriquecido.adversarios_probables?.length > 0 && (
            <div className="mt-3">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Adversarios probables</div>
              <div className="flex flex-wrap gap-1.5">
                {fase03.campo_electoral_enriquecido.adversarios_probables.map((a, i) => (
                  <Badge key={i} variant="outline">{a}</Badge>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Escenarios */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { label: "Escenario base", value: fase03.escenario_base, tone: "border-slate-300 bg-white" },
          { label: "Escenario optimista", value: fase03.escenario_optimista, tone: "border-emerald-300 bg-emerald-50/50" },
          { label: "Escenario pesimista", value: fase03.escenario_pesimista, tone: "border-red-300 bg-red-50/50" },
        ].map((s) => (
          <div key={s.label} className={`border rounded-lg p-4 ${s.tone}`}>
            <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold">{s.label}</div>
            <p className="text-sm text-slate-800 mt-2 leading-relaxed">{s.value || "—"}</p>
          </div>
        ))}
      </div>

      {/* Recomendaciones */}
      {fase03.recomendaciones_estrategicas?.length > 0 && (
        <Card className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Recomendaciones estratégicas</div>
          <div className="space-y-2">
            {fase03.recomendaciones_estrategicas.map((r, i) => (
              <div key={i} className="flex items-start gap-3 border-b border-slate-100 last:border-0 py-2">
                <Badge className={
                  r.prioridad === "alta" ? "bg-red-100 text-red-800" :
                  r.prioridad === "media" ? "bg-amber-100 text-amber-800" :
                  "bg-slate-100 text-slate-700"
                }>{r.prioridad}</Badge>
                <div className="flex-1">
                  <div className="text-sm text-slate-800">{r.accion}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Plazo: {r.plazo || "—"} · Responsable: {r.responsable || "—"}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Hoja de ruta 90 días */}
      {fase03.hoja_ruta_90_dias?.length > 0 && (
        <Card className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Hoja de ruta · 90 días</div>
          <div className="space-y-3">
            {fase03.hoja_ruta_90_dias.map((h, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-24 shrink-0 text-xs font-mono text-slate-500 border border-slate-200 rounded px-2 py-1 text-center">Día {h.dia}</div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-900">{h.objetivo}</div>
                  <div className="text-xs text-slate-500 mt-0.5">KPI: {h.kpi}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 mt-2" />
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Veredicto final */}
      {fase03.veredicto_final && (
        <Card className={`p-6 border ${
          fase03.veredicto_final.resultado === "AVANZAR" ? "border-emerald-300 bg-emerald-50/60"
          : fase03.veredicto_final.resultado === "AVANZAR CON CONDICIONES" ? "border-amber-300 bg-amber-50/60"
          : "border-red-300 bg-red-50/60"
        }`}>
          <div className="text-xs uppercase tracking-widest text-slate-600">Veredicto final integrado</div>
          <div className="font-display font-black text-2xl text-slate-900 mt-1">{fase03.veredicto_final.resultado}</div>
          <p className="text-slate-800 text-sm mt-3 leading-relaxed">{fase03.veredicto_final.sintesis}</p>
        </Card>
      )}
    </div>
  );
}

/* ============================ Shared helpers ============================ */
function ErrorCard({ label, msg, raw }) {
  return (
    <Card className="p-6 border-red-200 bg-red-50">
      <div className="flex items-center gap-2 text-red-700 font-display font-bold">
        <AlertTriangle className="w-5 h-5" /> {label}
      </div>
      <p className="text-sm mt-2 text-red-800">{msg}</p>
      {raw && <pre className="text-xs mt-3 whitespace-pre-wrap text-red-900 max-h-40 overflow-y-auto">{raw}</pre>}
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function MetaBox({ label, value }) {
  return (
    <div className="border border-slate-200 rounded-md p-2">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-slate-800 text-sm mt-0.5">{value || "—"}</div>
    </div>
  );
}

function DVPReport({ report }) {
  const printReport = () => {
    const w = window.open("", "_blank");
    if (!w) return toast.error("Permite ventanas emergentes para descargar");
    w.document.write(renderPrintHTML(report));
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  if (report.error) {
    return <ErrorCard label="Error del modelo" msg={report.error} raw={report.raw_response} />;
  }

  const veredicto = report.veredicto?.resultado || "—";
  const vColor = veredicto.includes("AVANZAR CON") ? "bg-amber-100 text-amber-800 border-amber-200"
    : veredicto === "AVANZAR" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : "bg-red-100 text-red-800 border-red-200";

  return (
    <Card className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Reporte DVP v2.1</div>
          <h2 className="font-display font-bold text-xl text-slate-900">{report.meta?.aspirante || "—"}</h2>
        </div>
        <Button size="sm" variant="outline" onClick={printReport} data-testid="dvp-print">
          <Download className="w-4 h-4 mr-1" /> Descargar
        </Button>
      </div>

      <div className={`border rounded-lg p-4 ${vColor}`}>
        <div className="text-xs uppercase tracking-widest opacity-70">Veredicto</div>
        <div className="font-display font-bold text-2xl mt-1">{veredicto}</div>
        <p className="text-sm mt-2 leading-relaxed">{report.veredicto?.justificacion}</p>
        {report.veredicto?.condiciones?.length > 0 && (
          <div className="mt-3 text-sm">
            <div className="font-semibold mb-1">Condiciones:</div>
            <ul className="list-disc list-inside space-y-0.5">
              {report.veredicto.condiciones.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        )}
      </div>

      {report.correccion_encuadre?.requerida && (
        <div className="border rounded-lg p-3 border-amber-200 bg-amber-50/60">
          <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold mb-2">⚠ Corrección de encuadre requerida</div>
          <div className="text-sm text-slate-700">{report.correccion_encuadre.descripcion}</div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 text-sm">
        <MetaBox label="Cargo declarado" value={report.meta?.cargo_declarado} />
        <MetaBox label="Cargo verificado" value={report.meta?.cargo_verificado} />
        <MetaBox label="Partido" value={report.meta?.partido_declarado} />
        <MetaBox label="Distrito/Municipio" value={report.meta?.distrito_o_municipio} />
      </div>

      <SimpleSection title="Perfil verificado" items={report.perfil_verificado?.antecedentes} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SimpleSection title="Activos verificados" items={report.activos?.verificados} tone="border-emerald-200 bg-emerald-50/40" />
        <SimpleSection title="Activos declarados" items={report.activos?.declarados} />
        <SimpleSection title="Riesgos verificados" items={report.riesgos?.verificados} tone="border-red-200 bg-red-50/40" />
        <SimpleSection title="Riesgos potenciales" items={report.riesgos?.potenciales} />
      </div>

      {report.frase_cierre && (
        <div className="border-t border-slate-200 pt-4 mt-2">
          <div className="text-xs uppercase tracking-widest text-slate-500">Frase de cierre</div>
          <p className="text-slate-800 italic mt-1 leading-relaxed">{report.frase_cierre.linea_1}</p>
          <p className="text-slate-800 italic leading-relaxed">{report.frase_cierre.linea_2}</p>
        </div>
      )}
    </Card>
  );
}

function SimpleSection({ title, items, tone = "border-slate-200 bg-white" }) {
  return (
    <div className={`border rounded-lg p-3 ${tone}`}>
      <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold mb-2">{title}</div>
      {!items || items.length === 0 ? (
        <div className="text-xs text-slate-400 italic">Sin datos.</div>
      ) : (
        <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
          {items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      )}
    </div>
  );
}

function renderPrintHTML(report) {
  const j = (v) => (v || "—");
  const list = (arr) => (arr && arr.length ? `<ul>${arr.map((x) => `<li>${x}</li>`).join("")}</ul>` : "<em>Sin datos</em>");
  return `
<!doctype html>
<html><head><meta charset="utf-8"><title>DVP - ${j(report.meta?.aspirante)}</title>
<style>
body{font-family:Georgia,serif;color:#111;max-width:800px;margin:40px auto;padding:0 20px;line-height:1.5}
h1{font-size:24px;margin:0 0 4px}h2{font-size:16px;margin:24px 0 8px;padding-bottom:4px;border-bottom:1px solid #ccc}
.veredicto{padding:16px;border-radius:8px;background:#fff8e1;border:1px solid #f59e0b;margin:16px 0}
.veredicto .r{font-size:20px;font-weight:bold}
.meta{display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:13px}
.meta div{padding:6px;border:1px solid #ddd;border-radius:4px}
.meta span{color:#666;font-size:10px;text-transform:uppercase;display:block}
.frase{font-style:italic;border-top:1px solid #ccc;padding-top:12px;margin-top:24px}
</style></head><body>
<div style="font-size:11px;text-transform:uppercase;letter-spacing:2px;color:#666">Reporte DVP v2.1 · Noctua 360</div>
<h1>${j(report.meta?.aspirante)}</h1>
<div style="font-size:13px;color:#555">${j(report.meta?.cargo_verificado || report.meta?.cargo_declarado)} · ${j(report.meta?.distrito_o_municipio)}</div>

<div class="veredicto">
  <div style="font-size:10px;text-transform:uppercase;letter-spacing:2px;color:#666">Veredicto</div>
  <div class="r">${j(report.veredicto?.resultado)}</div>
  <p>${j(report.veredicto?.justificacion)}</p>
  ${report.veredicto?.condiciones?.length ? `<div><strong>Condiciones:</strong>${list(report.veredicto.condiciones)}</div>` : ""}
</div>

<h2>Metadatos</h2>
<div class="meta">
  <div><span>Cargo declarado</span>${j(report.meta?.cargo_declarado)}</div>
  <div><span>Cargo verificado</span>${j(report.meta?.cargo_verificado)}</div>
  <div><span>Partido</span>${j(report.meta?.partido_declarado)}</div>
  <div><span>Distrito/Municipio</span>${j(report.meta?.distrito_o_municipio)}</div>
</div>

<h2>Perfil verificado</h2>${list(report.perfil_verificado?.antecedentes)}
<h2>Activos verificados</h2>${list(report.activos?.verificados)}
<h2>Activos declarados</h2>${list(report.activos?.declarados)}
<h2>Riesgos verificados</h2>${list(report.riesgos?.verificados)}
<h2>Riesgos potenciales</h2>${list(report.riesgos?.potenciales)}

<div class="frase">
  <div style="font-size:10px;text-transform:uppercase;letter-spacing:2px;color:#666">Frase de cierre</div>
  <p>${j(report.frase_cierre?.linea_1)}</p>
  <p>${j(report.frase_cierre?.linea_2)}</p>
</div>
</body></html>`;
}

/* ================================ FASE 00 ================================ */
const FASE00_TABS = [
  { key: "mapa", label: "Mapa Electoral", icon: MapIcon, testid: "fase00-tab-mapa" },
  { key: "lectura", label: "Lectura Territorial", icon: Layers, testid: "fase00-tab-lectura" },
  { key: "inteligencia", label: "Panel de Inteligencia", icon: Activity, testid: "fase00-tab-inteligencia" },
  { key: "escenarios", label: "Motor de Escenarios", icon: Calculator, testid: "fase00-tab-escenarios" },
];

function scopeLabel(sub) {
  if (!sub) return "";
  const st = sub.scope_type || sub.scope;
  if (st === "estatal") return `ESTATAL · ${sub.entidad_nombre || ""}`;
  if (st === "municipal") return `MUNICIPAL · ${sub.municipio_nombre || ""}, ${sub.entidad_nombre || ""}`;
  if (st === "distrital_federal") return `DISTRITAL FEDERAL · Distrito ${sub.distrito_clave} · ${sub.entidad_nombre || ""}`;
  if (st === "distrital_local") return `DISTRITAL LOCAL · Distrito ${sub.distrito_clave} · ${sub.entidad_nombre || ""}`;
  return (st || "").toUpperCase();
}

function Fase00Inteligencia() {
  const { subaccount } = useAuth();
  const [tab, setTab] = useState("mapa");
  const [partidos, setPartidos] = useState([]);
  const [clientParty, setClientParty] = useState(null);
  const [allStates, setAllStates] = useState([]);
  const [districtMapping, setDistrictMapping] = useState(null); // {municipios_claves, secciones, ...} or null

  const pinnedState = subaccount?.entidad_clave || 8;

  const loadPartidos = async () => {
    const r = await api.get("/noctua/partidos");
    setPartidos(r.data);
    const c = r.data.find((p) => p.is_client);
    if (c) setClientParty(c.clave);
  };

  useEffect(() => {
    loadPartidos();
    api.get("/catalog/states").then((r) => setAllStates(r.data)).catch(() => {});
  }, []);

  // Fetch district mapping when scope is distrital
  useEffect(() => {
    const st = subaccount?.scope_type;
    if ((st === "distrital_federal" || st === "distrital_local") && subaccount?.entidad_clave && subaccount?.distrito_clave) {
      const tipo = st === "distrital_federal" ? "federal" : "local";
      api.get(`/noctua/distritos/${subaccount.entidad_clave}/${tipo}/${subaccount.distrito_clave}`)
        .then((r) => setDistrictMapping(r.data))
        .catch(() => setDistrictMapping(null));
    } else {
      setDistrictMapping(null);
    }
  }, [subaccount?.scope_type, subaccount?.entidad_clave, subaccount?.distrito_clave]);

  const setClient = async (clave) => {
    await api.post(`/noctua/partidos/${clave}/set-client`);
    setClientParty(clave);
    loadPartidos();
  };

  // Pin selectedState to the subaccount's entidad; ignore attempts to change it.
  const scopeFilter = subaccount ? {
    scope_type: subaccount.scope_type || subaccount.scope,
    entidad_clave: subaccount.entidad_clave,
    entidad_nombre: subaccount.entidad_nombre,
    municipio_clave: subaccount.municipio_clave,
    municipio_nombre: subaccount.municipio_nombre,
    distrito_clave: subaccount.distrito_clave,
    // District mapping (loaded async): list of municipio_claves in the district
    district_municipios_claves: districtMapping?.municipios_claves || null,
    district_secciones: districtMapping?.secciones || null,
    district_mapping_loaded: !!districtMapping,
  } : null;

  const ctxValue = {
    partidos,
    clientParty,
    refreshPartidos: loadPartidos,
    setClient,
    selectedState: pinnedState,
    setSelectedState: () => {},
    allStates,
    scopeFilter,
  };

  return (
    <NoctuaContext.Provider value={ctxValue}>
      <div className="noctua-bg rounded-lg overflow-hidden -mx-2 fase00-pinned" data-testid="fase00-shell">
        {/* Header + scope banner */}
        <div className="border-b border-slate-800/60 px-6 py-4 bg-slate-950/60">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 grid place-items-center rounded bg-gradient-to-br from-blue-500 to-blue-700">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-display font-black text-white text-base leading-none tracking-tight">
                  NOCTUA · Inteligencia Electoral
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-blue-400 mt-1">
                  Adscripción: <span className="text-blue-200">{scopeLabel(subaccount) || "Sin definir"}</span>
                </div>
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">
              Partido cliente: <span className="text-white ml-1">{clientParty || "—"}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-800/60 bg-slate-950/40 px-6">
          <div className="flex gap-6 overflow-x-auto">
            {FASE00_TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`noctua-tab flex items-center gap-2 whitespace-nowrap ${active ? "active" : ""}`}
                  data-testid={t.testid}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 min-h-[500px]">
          {tab === "mapa" && <MapaPage />}
          {tab === "lectura" && <LecturaPage />}
          {tab === "inteligencia" && <InteligenciaPage />}
          {tab === "escenarios" && <EscenariosPage />}
        </div>
      </div>
    </NoctuaContext.Provider>
  );
}

